import { useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import AccountLayout from '@/layouts/AccountLayout'
import PageHeader from '@/components/PageHeader'
import QuestionBuilder from '@/components/QuestionBuilder'
import type { Question } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface TemplateDetail {
  id: number
  name: string
  description: string | null
  question_count: number
  survey_count: number
  questions: Question[]
}

interface EditProps {
  template: TemplateDetail
}

export default function Edit({ template }: EditProps) {
  const ap = useAccountPath()
  const { data, setData, patch, processing } = useForm({
    name: template.name,
    description: template.description ?? '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch(ap(`/survey_templates/${template.id}`))
  }

  return (
    <AccountLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <PageHeader
          title={template.name}
          breadcrumbs={[
            { label: 'Templates', href: ap('/survey_templates') },
            { label: template.name },
          ]}
        />

        {/* Name & description */}
        <form onSubmit={handleSubmit}>
          <div className="card bg-base-100">
            <div className="card-body space-y-4">
              <h2 className="text-lg font-semibold">Template Details</h2>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Name</legend>
                <input
                  type="text"
                  className="input w-full"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  required
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Description</legend>
                <textarea
                  className="textarea w-full"
                  rows={2}
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder="Brief description of what this template measures..."
                />
              </fieldset>

              <div className="flex justify-end">
                <button type="submit" className="btn btn-primary btn-sm" disabled={processing || !data.name.trim()}>
                  {processing && <span className="loading loading-spinner loading-sm" />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Questions — full CRUD with drag-and-drop */}
        <QuestionBuilder
          surveyId={template.id}
          questions={template.questions}
          locked={false}
          basePath={ap(`/survey_templates/${template.id}`)}
        />
      </div>
    </AccountLayout>
  )
}
